import React, { useEffect, useRef } from "react";

export default function AnimatedBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const embers = [];
    const emberCount = 80;
    const bytes = [];
    const byteCount = 30;

    // Ember particles (glowing fire particles)
    class Ember {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = canvas.height + Math.random() * 100;
        this.size = Math.random() * 3 + 1;
        this.speedY = -(Math.random() * 0.5 + 0.3);
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.opacity = Math.random() * 0.5 + 0.3;
        this.life = 1;
        this.decay = Math.random() * 0.003 + 0.001;
        this.glow = Math.random() * 10 + 5;
      }

      update() {
        this.y += this.speedY;
        this.x += this.speedX;
        this.life -= this.decay;
        this.opacity = this.life * 0.6;
        this.glow = this.life * 15;

        if (this.life <= 0 || this.y < -50) {
          this.reset();
        }
      }

      draw() {
        // Glow effect
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size * 3
        );
        gradient.addColorStop(0, `rgba(255, 140, 50, ${this.opacity})`);
        gradient.addColorStop(0.5, `rgba(255, 100, 30, ${this.opacity * 0.5})`);
        gradient.addColorStop(1, 'rgba(255, 80, 20, 0)');

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Core ember
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 200, 100, ${this.opacity * 1.5})`;
        ctx.shadowBlur = this.glow;
        ctx.shadowColor = '#ff6b35';
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    // Digital bytes (binary code streams)
    class Byte {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = -Math.random() * canvas.height;
        this.speed = Math.random() * 1 + 0.5;
        this.opacity = Math.random() * 0.3 + 0.2;
        this.char = Math.random() > 0.5 ? '1' : '0';
        this.size = Math.random() * 12 + 10;
      }

      update() {
        this.y += this.speed;

        if (this.y > canvas.height + 50) {
          this.reset();
        }
      }

      draw() {
        ctx.font = `${this.size}px monospace`;
        ctx.fillStyle = `rgba(0, 212, 255, ${this.opacity})`;
        ctx.shadowBlur = 5;
        ctx.shadowColor = '#00d4ff';
        ctx.fillText(this.char, this.x, this.y);
        ctx.shadowBlur = 0;
      }
    }

    // Create embers
    for (let i = 0; i < emberCount; i++) {
      embers.push(new Ember());
    }

    // Create bytes
    for (let i = 0; i < byteCount; i++) {
      bytes.push(new Byte());
    }

    // Draw circuit lines
    function drawCircuitLines() {
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.1)';
      ctx.lineWidth = 1;

      // Horizontal lines
      for (let i = 0; i < 5; i++) {
        const y = (canvas.height / 5) * i;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Vertical lines
      for (let i = 0; i < 8; i++) {
        const x = (canvas.width / 8) * i;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }

      // Circuit nodes
      ctx.fillStyle = 'rgba(0, 212, 255, 0.2)';
      for (let i = 0; i < 5; i++) {
        for (let j = 0; j < 8; j++) {
          const x = (canvas.width / 8) * j;
          const y = (canvas.height / 5) * i;
          ctx.beginPath();
          ctx.arc(x, y, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw circuit pattern (static)
      drawCircuitLines();

      // Update and draw bytes (digital element)
      bytes.forEach(byte => {
        byte.update();
        byte.draw();
      });

      // Update and draw embers (fire element)
      embers.forEach(ember => {
        ember.update();
        ember.draw();
      });

      requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none"
      }}
    />
  );
}
